import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  normalizeDeliveryLocation,
  reverseGeocodeCoordinates,
  saveDeliveryLocation,
  clearSavedDeliveryLocation,
} from '../lib/deliveryLocation';
import LocationPinIcon from './LocationPinIcon';

const blankLocation = {
  id: null,
  nickname: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  country: 'India',
  detectedLabel: '',
  source: 'manual',
  coordinates: null,
  verificationStatus: '',
  verificationNote: '',
  isDefault: false,
};

const displayLocation = (location) => [location.city, location.state, location.zip].filter(Boolean).join(', ');

const asLocation = (location) => normalizeDeliveryLocation({ ...blankLocation, ...(location || {}) });

const getErrorMessage = async (response, fallback) => {
  try {
    const data = await response.json();
    return data.msg || fallback;
  } catch {
    return fallback;
  }
};

export default function LocationPickerModal({ open, onClose, savedLocation, onSaved, token }) {
  const closeButtonRef = useRef(null);
  const wasOpenRef = useRef(false);
  const [draft, setDraft] = useState(() => asLocation(savedLocation));
  const [addresses, setAddresses] = useState([]);
  const [status, setStatus] = useState(null);
  const [pinLookupState, setPinLookupState] = useState('idle');
  const [isDetecting, setIsDetecting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [makeDefault, setMakeDefault] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return undefined;
    }
    if (wasOpenRef.current) return undefined;
    wasOpenRef.current = true;
    setDraft(asLocation(savedLocation));
    setStatus(null);
    setPinLookupState(savedLocation?.zip?.length === 6 ? 'idle' : 'idle');
    setShowForm(true);
    setMakeDefault(false);
    window.setTimeout(() => closeButtonRef.current?.focus(), 0);
    return undefined;
  }, [open, savedLocation]);

  useEffect(() => {
    if (!open || !token) {
      if (open && !token) {
        setAddresses([]);
        setIsLoadingAddresses(false);
      }
      return undefined;
    }

    const controller = new AbortController();
    setIsLoadingAddresses(true);
    fetch('/api/addresses', {
      headers: { 'x-auth-token': token },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(await getErrorMessage(response, 'Unable to load saved addresses.'));
        return response.json();
      })
      .then((data) => {
        const loaded = Array.isArray(data.addresses) ? data.addresses.map(asLocation) : [];
        setAddresses(loaded);
        setShowForm(loaded.length === 0);
        const preferred = loaded.find((address) => address.isDefault) || loaded[0];
        if (preferred) {
          const cached = saveDeliveryLocation(preferred);
          onSaved(cached);
          setDraft(cached);
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') setStatus({ type: 'error', message: error.message });
      })
      .finally(() => setIsLoadingAddresses(false));

    return () => controller.abort();
  }, [open, token, onSaved]);

  useEffect(() => {
    if (!open || draft.zip.length !== 6) {
      if (draft.zip.length !== 6) setPinLookupState('idle');
      return undefined;
    }

    const controller = new AbortController();
    setPinLookupState('loading');
    setStatus({ type: 'info', message: 'Checking PIN code and filling the city and state…' });
    fetch(`/api/addresses/pincode/${draft.zip}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(await getErrorMessage(response, 'PIN code could not be verified.'));
        return response.json();
      })
      .then((data) => {
        setDraft((current) => ({
          ...current,
          city: data.city || '',
          state: data.state || '',
          country: data.country || 'India',
          verificationStatus: data.verificationStatus || 'verified',
          verificationNote: data.verificationNote || '',
        }));
        setPinLookupState('verified');
        setStatus({ type: 'success', message: `PIN verified for ${data.city}, ${data.state}.` });
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setPinLookupState(error.message.includes('unavailable') ? 'unavailable' : 'not_found');
        setStatus({ type: 'error', message: error.message });
      });

    return () => controller.abort();
  }, [open, draft.zip]);

  useEffect(() => {
    if (!open) return undefined;
    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!open) return null;

  const handleFieldChange = (event) => {
    const { name } = event.target;
    const value = name === 'zip'
      ? event.target.value.replace(/\D/g, '').slice(0, 6)
      : event.target.value;
    setDraft((current) => ({
      ...current,
      [name]: value,
      ...(name === 'zip' ? { city: '', state: '', verificationStatus: '', verificationNote: '' } : {}),
      source: 'manual',
    }));
    setStatus(null);
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setStatus({ type: 'error', message: 'Location detection is not available in this browser. Enter your PIN below.' });
      return;
    }

    setIsDetecting(true);
    setStatus({ type: 'info', message: 'Allow location access in your browser to find your nearby address.' });
    navigator.geolocation.getCurrentPosition(async ({ coords }) => {
      const coordinates = { latitude: coords.latitude, longitude: coords.longitude };
      setDraft((current) => ({ ...current, coordinates, source: 'browser' }));
      try {
        const detected = await reverseGeocodeCoordinates(coordinates);
        setDraft((current) => ({ ...current, ...detected, coordinates, source: 'browser' }));
        setStatus({
          type: 'success',
          message: detected.city || detected.zip
            ? `We found ${displayLocation(detected)}. Confirm the PIN before saving.`
            : 'Location permission granted. Confirm or complete the PIN below before saving.',
        });
        setShowForm(true);
      } catch {
        setStatus({ type: 'info', message: 'Location permission granted, but the address could not be filled automatically. Enter your PIN below.' });
      } finally {
        setIsDetecting(false);
      }
    }, (error) => {
      const message = error.code === 1
        ? 'Location permission was denied. You can enter your PIN manually below.'
        : 'We could not detect your location. Enter your PIN manually below.';
      setStatus({ type: 'error', message });
      setIsDetecting(false);
    }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalized = asLocation(draft);
    if (!/^\d{6}$/.test(normalized.zip)) {
      setStatus({ type: 'error', message: 'Enter a valid 6-digit PIN code.' });
      return;
    }
    if (pinLookupState !== 'verified') {
      setStatus({ type: 'error', message: 'Verify the PIN code before saving this address.' });
      return;
    }

    setIsSaving(true);
    try {
      if (!token) {
        const localOnly = saveDeliveryLocation(normalized);
        onSaved(localOnly);
        setStatus({ type: 'info', message: 'Location saved on this device. Sign in to save multiple addresses to your account.' });
        onClose();
        return;
      }

      const response = await fetch('/api/addresses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
        body: JSON.stringify({
          nickname: normalized.nickname,
          address: normalized.address,
          city: normalized.city,
          state: normalized.state,
          zip: normalized.zip,
          isDefault: makeDefault,
        }),
      });
      if (!response.ok) throw new Error(await getErrorMessage(response, 'Unable to save this address.'));
      const data = await response.json();
      const saved = asLocation(data.address);
      setAddresses((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
      const cached = saveDeliveryLocation(saved);
      onSaved(cached);
      onClose();
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUseSavedAddress = async (address) => {
    if (!token || !address.id) return;
    setStatus({ type: 'info', message: 'Selecting this address…' });
    try {
      const response = await fetch(`/api/addresses/${address.id}/default`, {
        method: 'PATCH',
        headers: { 'x-auth-token': token },
      });
      if (!response.ok) throw new Error(await getErrorMessage(response, 'Unable to select this address.'));
      const data = await response.json();
      const selected = asLocation(data.address || address);
      setAddresses((current) => current.map((item) => ({ ...item, isDefault: item.id === selected.id })));
      const cached = saveDeliveryLocation(selected);
      onSaved(cached);
      onClose();
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    }
  };

  const handleDeleteAddress = async (address) => {
    if (!token || !address.id || !window.confirm(`Delete ${address.nickname || displayLocation(address)}?`)) return;
    try {
      const response = await fetch(`/api/addresses/${address.id}`, {
        method: 'DELETE',
        headers: { 'x-auth-token': token },
      });
      if (!response.ok) throw new Error(await getErrorMessage(response, 'Unable to delete this address.'));
      const remaining = addresses.filter((item) => item.id !== address.id);
      const replacement = remaining.find((item) => item.isDefault) || remaining[0];
      setAddresses(remaining.map((item) => ({ ...item, isDefault: replacement ? item.id === replacement.id : false })));
      if (savedLocation?.id === address.id) {
        if (replacement) {
          const cached = saveDeliveryLocation(replacement);
          onSaved(cached);
        } else {
          clearSavedDeliveryLocation();
          onSaved(null);
        }
      }
      if (remaining.length === 0) {
        setDraft(asLocation(blankLocation));
        setShowForm(true);
      }
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    }
  };

  const startNewAddress = () => {
    setDraft(asLocation(blankLocation));
    setMakeDefault(addresses.length === 0);
    setPinLookupState('idle');
    setStatus(null);
    setShowForm(true);
  };

  return (
    <div className="location-picker-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="location-picker-modal" role="dialog" aria-modal="true" aria-labelledby="location-picker-title">
        <header className="location-picker-modal__header">
          <div>
            <p className="location-picker-modal__eyebrow">Delivery location</p>
            <h2 id="location-picker-title">Where should we deliver?</h2>
          </div>
          <button ref={closeButtonRef} type="button" className="location-picker-modal__close" onClick={onClose} aria-label="Close location picker">×</button>
        </header>

        <p className="location-picker-modal__intro">
          Use your PIN to fill the city and state automatically. A full street address is optional here and can be completed at checkout.
        </p>

        {!token && (
          <div className="location-picker-account-note">
            <strong>Sign in to save more than one address.</strong>
            <button type="button" className="btn btn-link p-0" onClick={() => navigate('/login')}>Sign in</button>
          </div>
        )}

        {isLoadingAddresses && <p className="location-picker-loading" role="status">Loading saved addresses…</p>}

        {addresses.length > 0 && (
          <div className="location-picker-addresses">
            <div className="location-picker-section-heading">
              <h3>Saved addresses</h3>
              <span>{addresses.length}</span>
            </div>
            <div className="location-picker-address-list">
              {addresses.map((address) => (
                <article className={`location-picker-address-card${address.isDefault ? ' is-default' : ''}`} key={address.id}>
                  <div className="location-picker-address-card__body">
                    <strong>{address.nickname || displayLocation(address) || 'Saved address'}</strong>
                    <span>{displayLocation(address)}</span>
                    {address.address && <span>{address.address}</span>}
                    <small>{address.verificationStatus === 'corrected' ? 'PIN checked; city/state corrected' : 'PIN checked'} </small>
                  </div>
                  <div className="location-picker-address-card__actions">
                    {address.isDefault && <span className="location-picker-default-label">Selected</span>}
                    {!address.isDefault && <button type="button" className="btn btn-sm btn-outline-custom" onClick={() => handleUseSavedAddress(address)}>Use</button>}
                    <button type="button" className="btn btn-sm btn-link text-danger" onClick={() => handleDeleteAddress(address)}>Delete</button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}

        {addresses.length > 0 && !showForm && (
          <button type="button" className="btn btn-outline-custom location-picker-add" onClick={startNewAddress}>+ Add another address</button>
        )}

        {showForm && (
          <>
            <button type="button" className="btn btn-outline-custom location-picker-current" onClick={handleUseCurrentLocation} disabled={isDetecting}>
              <LocationPinIcon className="location-picker-current__icon" />
              {isDetecting ? 'Finding your location…' : 'Use my current location'}
            </button>

            <div className="location-picker-divider"><span>or enter a PIN</span></div>

            <form onSubmit={handleSubmit}>
              <div className="location-picker-fields">
                <div className="location-picker-field location-picker-field--pin">
                  <label htmlFor="location-picker-zip">PIN code *</label>
                  <input id="location-picker-zip" name="zip" type="text" inputMode="numeric" maxLength="6" autoComplete="postal-code" value={draft.zip} onChange={handleFieldChange} required aria-describedby="location-picker-pin-status" />
                  <small id="location-picker-pin-status" className={`location-picker-pin-state location-picker-pin-state--${pinLookupState}`}>
                    {pinLookupState === 'loading' && 'Checking PIN…'}
                    {pinLookupState === 'verified' && 'City and state filled from PIN'}
                    {pinLookupState === 'unavailable' && 'Try again when PIN lookup is available'}
                    {pinLookupState === 'not_found' && 'Check the six digits'}
                  </small>
                </div>
                <div className="location-picker-field">
                  <label htmlFor="location-picker-city">City {pinLookupState === 'verified' ? '(from PIN)' : ''}</label>
                  <input id="location-picker-city" name="city" type="text" autoComplete="address-level2" value={draft.city} onChange={handleFieldChange} readOnly={pinLookupState === 'verified'} />
                </div>
                <div className="location-picker-field">
                  <label htmlFor="location-picker-state">State {pinLookupState === 'verified' ? '(from PIN)' : ''}</label>
                  <input id="location-picker-state" name="state" type="text" autoComplete="address-level1" value={draft.state} onChange={handleFieldChange} readOnly={pinLookupState === 'verified'} />
                </div>
                <div className="location-picker-field location-picker-field--wide">
                  <label htmlFor="location-picker-address">Full address (optional)</label>
                  <textarea id="location-picker-address" name="address" rows="2" autoComplete="street-address" value={draft.address} onChange={handleFieldChange} placeholder="House / flat, street, landmark" />
                </div>
                <div className="location-picker-field location-picker-field--wide">
                  <label htmlFor="location-picker-nickname">Address nickname (optional)</label>
                  <input id="location-picker-nickname" name="nickname" type="text" maxLength="80" value={draft.nickname} onChange={handleFieldChange} placeholder="Home, Work, Parents" />
                </div>
              </div>

              {token && addresses.length > 0 && (
                <label className="location-picker-default-check">
                  <input type="checkbox" checked={makeDefault} onChange={(event) => setMakeDefault(event.target.checked)} />
                  <span>Make this my selected address</span>
                </label>
              )}

              {status && <p className={`location-picker-status location-picker-status--${status.type}`} role="status">{status.message}</p>}

              <div className="location-picker-actions">
                {addresses.length > 0 && <button type="button" className="btn btn-outline-custom" onClick={() => setShowForm(false)}>Back to saved</button>}
                <button type="button" className="btn btn-outline-custom" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary-custom" disabled={isSaving || pinLookupState !== 'verified'}>{isSaving ? 'Saving…' : 'Save address'}</button>
              </div>
            </form>
          </>
        )}

        {!showForm && status && <p className={`location-picker-status location-picker-status--${status.type}`} role="status">{status.message}</p>}
      </section>
    </div>
  );
}
